import { useContext, useEffect, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'

import clsx from 'clsx'
import { toast } from 'react-toastify'

import { Grid, Paper, useMediaQuery, useTheme } from '@material-ui/core'

import { AuthContext } from '../../context/Auth/AuthContext'
import { EditMessageProvider } from '../../context/EditingMessage/EditingMessageContext'
import { ReplyMessageProvider } from '../../context/ReplyingMessage/ReplyingMessageContext'
import { BotReplyProvider } from '../../context/BotReply/BotReplyContext'
import { SocketContext } from '../../context/Socket/SocketContext'
import toastError from '../../errors/toastError'
import useSettings from '../../hooks/useSettings'
import api from '../../services/api'
import ContactDrawer from '../ContactDrawer'
import MessageInput from '../MessageInputCustom/'
import MessagesList from '../MessagesList'
import NewTicketModalWecloud from '../NewTicketModalWecloud'
import TicketActionButtons from '../TicketActionButtonsCustom'
import TicketHeader from '../TicketHeader'
import TicketInfo from '../TicketInfo'

import ContactTags from '../ContactTags'
import TicketInfoDrawer from '../TicketInfoDrawer'
import { UploadDropZone } from '../UploadDropZone'
import { useStyles } from './style'

const Ticket = () => {
  const { ticketId } = useParams()
  const history = useHistory()
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const { user } = useContext(AuthContext)

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [infoDrawerOpen, setInfoDrawerOpen] = useState(false)
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false)
  const [contactTicket, setContactTicket] = useState(null)
  const messagesListRef = useRef(null)

  const [loading, setLoading] = useState(true)
  const [contact, setContact] = useState({})
  const [ticket, setTicket] = useState({})
  const [showTabGroups, setShowTabGroups] = useState(false)
  const { getSetting } = useSettings()
  // const [tags, setTags] = useState([])

  const socketManager = useContext(SocketContext)

  useEffect(() => {
    Promise.all([getSetting('CheckMsgIsGroup'), getSetting('groupsTab')]).then(
      ([ignoreGroups, groupsTab]) => {
        setShowTabGroups(ignoreGroups === 'disabled' && groupsTab === 'enabled')
      }
    )
  }, [])

  useEffect(() => {
    setLoading(true)
    const delayDebounceFn = setTimeout(() => {
      const fetchTicket = async () => {
        try {
          // Verificar se ticketId é um UUID ou um ID numérico
          const isUUID =
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
              ticketId
            )
          const endpoint = isUUID
            ? `/tickets/u/${ticketId}`
            : `/tickets/${ticketId}`

          const { data } = await api.get(endpoint)
          const { queueId, status, userId: ticketUserId } = data
          const { queues, profile } = user

          const queueAllowed = queues.find(q => q.id === queueId)
          const isPendingUnassigned = status === 'pending' && !ticketUserId
          if (
            !isPendingUnassigned &&
            queueAllowed === undefined &&
            profile !== 'admin'
          ) {
            toast.error('Acesso não permitido')
            history.push('/tickets')
            return
          }

          setContact(data.contact)
          setTicket(data)
          setLoading(false)
        } catch (err) {
          setLoading(false)
          toastError(err)
        }
      }
      fetchTicket()
    }, 500)
    return () => clearTimeout(delayDebounceFn)
  }, [ticketId, user, history])

  useEffect(() => {
    const companyId = localStorage.getItem('companyId')

    const socket = socketManager.GetSocket(companyId)

    const onConnectTicket = () => {
      socket.emit('joinChatBox', `${ticket.id}`)
    }

    socketManager.onConnect(onConnectTicket)

    const onCompanyTicket = data => {
      if (data.action === 'update' && data.ticket.id === ticket.id) {
        if (isMobile && ticket.status && data.ticket.status !== ticket.status) {
          history.push('/tickets')
          return
        }
        setTicket(data.ticket)
      }

      if (data.action === 'delete' && data.ticketId === ticket.id) {
        history.push('/tickets')
      }
    }

    const onCompanyContact = data => {
      if (data.action === 'update') {
        setContact(prevState => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact }
          }
          return prevState
        })
      }
    }

    socket.on(`company-${companyId}-ticket`, onCompanyTicket)
    socket.on(`company-${companyId}-contact`, onCompanyContact)

    return () => {
      socket.disconnect()
    }
  }, [ticketId, ticket, history, socketManager])

  const handleInfoDrawerOpen = () => {
    setDrawerOpen(prev => !prev)
  }

  const handleInfoDrawerClose = () => {
    setInfoDrawerOpen(false)
  }

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen)
  }

  // const handleDrawerOpen = () => {
  //   setDrawerOpen(true)
  // }

  const handleDrawerClose = () => {
    setDrawerOpen(false)
  }

  const handleOpenNewTicketForContact = contactData => {
    setContactTicket(contactData)
    setNewTicketModalOpen(true)
  }

  const handleCloseOrOpenTicket = createdTicket => {
    setNewTicketModalOpen(false)
    setContactTicket(null)

    if (createdTicket?.uuid) {
      history.push(`/tickets/${createdTicket.uuid}`)
      return
    }

    if (createdTicket?.id) {
      history.push(`/tickets/${createdTicket.id}`)
    }
  }

  const renderTicketInfo = () => {
    if (ticket.user !== undefined) {
      return (
        <TicketInfo
          contact={contact}
          ticket={ticket}
          onClick={toggleDrawer}
          isDrawerOpen={drawerOpen}
        />
      )
    }
  }

  const renderMessagesList = () => {
    return (
      <>
        <MessagesList
          ref={messagesListRef}
          ticket={ticket}
          ticketId={ticket.id}
          isGroup={ticket.isGroup}
          markAsRead={true}
          onOpenNewTicketForContact={handleOpenNewTicketForContact}
        ></MessagesList>
        <MessageInput
          ticket={ticket}
          showTabGroups
          parentDrawerOpen={drawerOpen}
          messagesListRef={messagesListRef}
        />
      </>
    )
  }

  return (
    <Grid container className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen || infoDrawerOpen
        })}
      >
        <TicketHeader loading={loading}>
          <Grid item xs={12} md={12} className={classes.ticketHeader}>
            {renderTicketInfo()}
            <TicketActionButtons
              ticket={ticket}
              showTabGroups={showTabGroups}
              onClick={handleInfoDrawerOpen}
            />
          </Grid>
        </TicketHeader>

        {/* <Paper>
          Tirando as tags do ticket 
          {!ticket.isGroup && <TagsContainer ticket={ticket} />}
        </Paper> */}

        {!isMobile &&
          !ticket.isGroup &&
          (ticket.status === 'open' || ticket.status === 'pending') && (
            <Paper elevation={0} className={classes.tagsContainer}>
              <ContactTags
                contact={contact}
                // handleRemoveTag={handleRemoveTag}
                // handleAddTag={handleAddTag}
                filterTags={['contato', 'atendimento']}
                showAllTypes={true}
                ticket={ticket}
              />
            </Paper>
          )}
        {/* <ReplyMessageProvider>
          <EditMessageProvider>{renderMessagesList()}</EditMessageProvider>
        </ReplyMessageProvider> */}

        <ReplyMessageProvider>
          <EditMessageProvider>
            <BotReplyProvider>
              {ticket && ticket.id ? (
                <UploadDropZone
                  ticket={ticket}
                  ticketId={ticket.id}
                  parentDrawerOpen={drawerOpen}
                >
                  {renderMessagesList()}
                </UploadDropZone>
              ) : (
                renderMessagesList()
              )}
            </BotReplyProvider>
          </EditMessageProvider>
        </ReplyMessageProvider>
      </Paper>
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        loading={loading}
        ticket={ticket}
      />

      <NewTicketModalWecloud
        modalOpen={newTicketModalOpen}
        initialContact={contactTicket}
        onClose={handleCloseOrOpenTicket}
      />

      {/* <TicketInfoDrawer
        open={infoDrawerOpen}
        handleDrawerClose={handleInfoDrawerClose}
        contact={contact}
        loading={loading}
        ticket={ticket}
      /> */}
      {/* {drawerOpen && <ChatDrawer />} */}
    </Grid>
  )
}

export default Ticket
